<template>
  <div style="padding: 20px; min-height: 630px; background-color: rgba(221, 225, 230, 1); border: solid 1px #ccc">
    <div class="metric-filters">
      <span class="title_bi metric-filters-title">Estado Impositivo</span>
      <div class="metric-filter metric-filter-empresa">
        <v-combobox v-model="emitter" autocomplete="off" item-text="name" item-value="id" :items="emitters" label="Empresa" class="pt-0 mt-0" dense hide-details @change="load"></v-combobox>
      </div>
      <v-btn-toggle v-model="period_type" mandatory dense class="metric-filter-toggle" @change="load">
        <v-btn small value="month">Mes</v-btn>
        <v-btn small value="year">Año</v-btn>
      </v-btn-toggle>
      <div v-if="period_type === 'month'" class="metric-filter metric-filter-mes">
        <v-combobox v-model="mes_selected" autocomplete="off" item-text="name" item-value="id" :items="months" label="Mes" class="pt-0 mt-0" dense hide-details @change="load"></v-combobox>
      </div>
      <div class="metric-filter metric-filter-anio">
        <v-combobox v-model="year" :items="years" label="Año" class="pt-0 mt-0" dense hide-details @change="load"></v-combobox>
      </div>
    </div>

    <p v-if="!emitter && !loading" class="caption mt-4">Seleccioná una empresa para ver su estado impositivo.</p>

    <!-- La grilla con titulos se ve siempre; mientras carga, cada valor muestra su skeleton. -->
    <template v-if="loading || data">
      <!-- Indicadores -->
      <div class="row">
        <div class="col-md-3" v-for="kpi in kpis" :key="kpi.title">
          <v-card class="metric-card metric-kpi-card">
            <p class="title_bi mb-0">{{ kpi.title }}</p>
            <p class="caption mb-0">{{ kpi.caption }}</p>
            <div class="metric-kpi">
              <p v-if="ready" class="text-h6 font-weight-bold mb-1" :style="kpi.color ? 'color:' + kpi.color : ''">{{ money(kpi.amount) }}</p>
              <v-skeleton-loader v-else type="heading" width="170" class="metric-skeleton-value mb-2"></v-skeleton-loader>
              <router-link v-if="kpi.link" :to="kpi.link" class="caption">{{ kpi.linkText || "Ver detalle" }}</router-link>
              <span v-else-if="ready && kpi.note" class="caption" :style="kpi.color ? 'color:' + kpi.color : ''">{{ kpi.note }}</span>
              <span v-else class="caption">&nbsp;</span>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Posicion de IVA y alicuotas -->
      <div class="row">
        <div class="col-md-6">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Posición de IVA</p>
              <p class="caption text-slate-500 mb-0">Saldo estimado del período, sin saldos a favor de períodos anteriores</p>
            </div>
            <div class="metric-card-body">
              <div v-for="linea in posicionIva" :key="linea.name" class="metric-line" :class="{ 'metric-line-total mb-2': linea.total }">
                <span class="metric-line-name" :class="{ 'font-weight-bold': linea.total }">{{ linea.name }}</span>
                <span v-if="ready" class="metric-line-amount" :style="linea.color ? 'color:' + linea.color : ''">{{ money(linea.amount) }}</span>
                <v-skeleton-loader v-else type="text" width="30%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
            </div>
          </v-card>
        </div>
        <div class="col-md-6">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">IVA por alícuota</p>
              <p class="caption text-slate-500 mb-0">Débito y crédito fiscal del período</p>
            </div>
            <div class="metric-card-body">
              <div class="metric-line metric-line-total mb-2">
                <span class="metric-line-name font-weight-bold">Alícuota</span>
                <span class="metric-line-amount metric-col">Débito</span>
                <span class="metric-line-amount metric-col">Crédito</span>
              </div>
              <template v-if="ready">
                <p v-if="iva.por_alicuota.length === 0" class="caption mb-0">Sin comprobantes en el período.</p>
                <div v-for="row in iva.por_alicuota" :key="row.id" class="metric-line">
                  <span class="metric-line-name">{{ row.name }}</span>
                  <span class="metric-line-amount metric-col">{{ money(row.debito) }}</span>
                  <span class="metric-line-amount metric-col">{{ money(row.credito) }}</span>
                </div>
              </template>
              <div v-else v-for="n in 3" :key="n" class="metric-line">
                <v-skeleton-loader type="text" width="20%" class="metric-skeleton-text"></v-skeleton-loader>
                <v-skeleton-loader type="text" width="25%" class="metric-skeleton-text"></v-skeleton-loader>
                <v-skeleton-loader type="text" width="25%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
              <router-link :to="'/' + Constant.SECTION_REPORT_IVA_DIGITAL" class="caption">Ver Libro IVA</router-link>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Evolucion -->
      <div class="row">
        <div class="col-md-12">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">IVA débito vs crédito</p>
              <p class="caption text-slate-500 mb-0">Últimos 12 meses hasta el período elegido, con el saldo técnico de cada mes</p>
            </div>
            <div class="metric-card-body pt-0">
              <apexchart v-if="ready" type="line" :height="chartHeight" :options="optionsEvolucion" :series="seriesEvolucion"></apexchart>
              <div v-else class="metric-skeleton-chart mt-3" :style="{ height: chartHeight - 30 + 'px' }">
                <div v-for="(bar, i) in skeletonBars" :key="i" class="metric-skeleton-group">
                  <div class="metric-skeleton-bar" :style="{ height: bar[0] + '%' }"></div>
                  <div class="metric-skeleton-bar" :style="{ height: bar[1] + '%' }"></div>
                </div>
              </div>
            </div>
          </v-card>
        </div>
      </div>

      <v-divider></v-divider>
      <p class="title_bi mb-0 mt-2">Retenciones y percepciones</p>
      <p class="caption mb-3">Lo sufrido es crédito a cuenta de cada impuesto; lo practicado como agente es plata de terceros a depositar</p>

      <!-- Rankings -->
      <div class="row" v-for="(fila, f) in rankings" :key="f">
        <div class="col-md-6" v-for="ranking in fila" :key="ranking.key">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">{{ ranking.title }}</p>
              <p class="caption text-slate-500 mb-0">{{ ranking.caption }}</p>
            </div>
            <div class="metric-card-body">
              <template v-if="ready">
                <p v-if="data[ranking.key].items.length === 0" class="caption mb-0">Sin movimientos en el período.</p>
                <div v-for="item in data[ranking.key].items" :key="String(item.id)" class="metric-share">
                  <div class="metric-line">
                    <span class="metric-line-name" :title="item.name">{{ item.name }}</span>
                    <span class="metric-line-amount">
                      {{ money(item.amount) }}
                      <span class="caption ml-1" style="color: #757575">{{ formatPercent(item.percent) }}</span>
                    </span>
                  </div>
                  <div class="metric-share-bar">
                    <div :style="{ width: barWidth(item.percent), backgroundColor: item.id === 'otros' ? '#B0BEC5' : ranking.color }"></div>
                  </div>
                </div>
                <div class="metric-line metric-line-total">
                  <span class="metric-line-name font-weight-bold">Total</span>
                  <span class="metric-line-amount">{{ money(data[ranking.key].total) }}</span>
                </div>
              </template>
              <template v-else>
                <div v-for="n in 5" :key="n" class="metric-line">
                  <v-skeleton-loader type="text" width="50%" class="metric-skeleton-text"></v-skeleton-loader>
                  <v-skeleton-loader type="text" width="25%" class="metric-skeleton-text"></v-skeleton-loader>
                </div>
              </template>
              <router-link v-if="ranking.link" :to="ranking.link" class="caption">Ver reporte</router-link>
            </div>
          </v-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { metricsService } from "@/libs/ws/metricsService.js";
import { emmiterService } from "@/libs/ws/emmiterService";
import { formatUtils } from "@/libs/utils/formatUtils";
import Constant from "@/constants/sections";

const COLOR_FAVOR = "#388E3C";
const COLOR_PAGAR = "#E53935";
const COLOR_AGENTE = "#1E88E5";

/** Importe abreviado para los ejes: 1.500.000 -> "1,5 M". */
function shortMoney(val) {
  const abs = Math.abs(val);
  if (abs >= 1e9) return (val / 1e9).toFixed(1).replace(".", ",") + " MM";
  if (abs >= 1e6) return (val / 1e6).toFixed(1).replace(".", ",") + " M";
  if (abs >= 1e3) return (val / 1e3).toFixed(0) + " K";
  return Math.round(val).toString();
}

const MONTHS = [
  { id: 1, name: "Enero" },
  { id: 2, name: "Febrero" },
  { id: 3, name: "Marzo" },
  { id: 4, name: "Abril" },
  { id: 5, name: "Mayo" },
  { id: 6, name: "Junio" },
  { id: 7, name: "Julio" },
  { id: 8, name: "Agosto" },
  { id: 9, name: "Septiembre" },
  { id: 10, name: "Octubre" },
  { id: 11, name: "Noviembre" },
  { id: 12, name: "Diciembre" },
];

export default {
  data() {
    const year = new Date().getFullYear();
    return {
      Constant: Constant,
      // Arranca en true: el skeleton se ve desde que se entra, mientras se cargan las empresas.
      loading: true,
      emitter: null,
      emitters: [],
      period_type: "month",
      months: MONTHS,
      mes_selected: MONTHS[new Date().getMonth()],
      year: year,
      years: Array.from({ length: 6 }, (_, i) => year - i),
      chartHeight: 280,
      // Alturas (%) de las barras debito / credito del skeleton del grafico.
      skeletonBars: [[55, 40], [70, 50], [45, 60], [80, 55], [60, 35], [75, 65], [50, 45], [85, 60], [65, 50], [40, 30], [70, 55], [60, 45]],
      rankings: [
        [
          { key: "creditos_por_impuesto", title: "Créditos a cuenta por impuesto", caption: "Percepciones en compras y retenciones en cobros sufridas", color: COLOR_FAVOR },
          { key: "iibb_por_jurisdiccion", title: "IIBB sufrido por jurisdicción", caption: "Percepciones y retenciones de Ingresos Brutos", color: COLOR_FAVOR },
        ],
        [
          {
            key: "retenciones_practicadas",
            title: "Retenciones practicadas",
            caption: "En pagos a proveedores, a depositar como agente",
            color: COLOR_AGENTE,
            link: "/" + Constant.SECTION_REPORT_RETENTIONS,
          },
          {
            key: "percepciones_practicadas",
            title: "Percepciones practicadas",
            caption: "En ventas, a depositar como agente",
            color: COLOR_AGENTE,
            link: "/" + Constant.SECTION_REPORT_PERCEPTIONS,
          },
        ],
      ],
      data: null,
    };
  },
  mounted() {
    emmiterService
      .filter({ enabled: true })
      .then((response) => {
        this.emitters = response.result;
        // Por defecto se elige la primera empresa.
        if (this.emitters.length > 0) {
          this.emitter = this.emitters[0];
          this.load();
        } else {
          this.loading = false;
        }
      })
      .catch(() => {
        this.loading = false;
      });
  },
  computed: {
    // Los titulos de las cards se muestran siempre; los valores solo cuando llegaron los datos.
    ready() {
      return !this.loading && this.data != null;
    },
    iva() {
      return this.ready ? this.data.iva : {};
    },
    kpis() {
      const k = this.ready ? this.data.kpis : {};
      return [
        {
          title: "Posición IVA",
          caption: "Saldo estimado del período",
          amount: k.posicion_iva,
          color: k.posicion_iva > 0 ? COLOR_PAGAR : COLOR_FAVOR,
          note: k.posicion_iva > 0 ? "A pagar" : "A favor",
        },
        { title: "Créditos a cuenta", caption: "Retenciones y percepciones sufridas", amount: k.creditos, color: COLOR_FAVOR },
        {
          title: "A depositar como agente",
          caption: "Retenciones y percepciones practicadas",
          amount: k.a_depositar,
          link: "/" + Constant.SECTION_REPORT_AGENTES_RECAUDACION,
          linkText: "Agentes de recaudación",
        },
        { title: "Base imponible IIBB", caption: "Ventas netas fiscales del período", amount: k.base_iibb },
      ];
    },
    posicionIva() {
      const i = this.iva;
      return [
        { name: "(+) Débito fiscal", amount: i.debito },
        { name: "(-) Crédito fiscal", amount: i.credito },
        { name: "Saldo técnico", amount: i.saldo_tecnico, total: true },
        { name: "(-) Percepciones de IVA sufridas", amount: i.percepciones_sufridas },
        { name: "(-) Retenciones de IVA sufridas", amount: i.retenciones_sufridas },
        { name: i.saldo > 0 ? "Saldo a pagar" : "Saldo a favor", amount: i.saldo, total: true, color: i.saldo > 0 ? COLOR_PAGAR : COLOR_FAVOR },
      ];
    },
    seriesEvolucion() {
      const e = this.iva.evolucion;
      return [
        { name: "Débito fiscal", type: "column", data: e.debito },
        { name: "Crédito fiscal", type: "column", data: e.credito },
        { name: "Saldo técnico", type: "line", data: e.saldo_tecnico },
      ];
    },
    optionsEvolucion() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        dataLabels: { enabled: false },
        stroke: { width: [0, 0, 2] },
        colors: [COLOR_PAGAR, COLOR_FAVOR, COLOR_AGENTE],
        legend: { horizontalAlign: "left" },
        labels: this.iva.evolucion.columnas.map((c) => c.name),
        yaxis: { labels: { formatter: shortMoney } },
        tooltip: { y: { formatter: this.money } },
      };
    },
  },
  methods: {
    money(value) {
      return formatUtils.formatPrice(value);
    },
    formatPercent(value) {
      return new Intl.NumberFormat("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value) + " %";
    },
    barWidth(percent) {
      return Math.max(0, Math.min(100, percent)) + "%";
    },
    load() {
      if (!this.emitter || !this.emitter.id) {
        this.data = null;
        return;
      }
      if (!this.year || (this.period_type === "month" && !(this.mes_selected && this.mes_selected.id))) return;

      const param = { emitter: this.emitter.id, period_type: this.period_type };
      if (this.period_type === "month") param.month = this.year + "-" + String(this.mes_selected.id).padStart(2, "0");
      else param.year = this.year;

      this.loading = true;
      metricsService
        .getEstadoImpositivo(param)
        .then((response) => {
          if (response.status === false) {
            this.$toast.error(response.msg, "Error");
            return;
          }
          this.data = response.result;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
